/**
 * FivePicSwiper组件 - PC端独有的多图滑动展示组件
 * 基于原Vue的FivePicSwiper组件迁移
 */

'use client';

import HomePageTitle from '@/app/(home-page)/components/HomePageTitle';
import MagicJump from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { getAlt } from '@/utils/tools';
import { RightArrowOutlined } from '@zeelool/icon';
import Image from '@/components/Image';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { Swiper, SwiperSlide } from 'swiper/react';
import type {
  FivePicSwiperIndexData,
  FivePicSwiperItem,
  FivePicSwiperProps,
  FivePicSwiperTrackingParams,
} from './types';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';

// 统一埋点参数Hook
const useTrackingParams = (
  item: FivePicSwiperItem,
  index: number,
  moduleIndex: number,
  componentId: string,
  moduleTitle: string,
  moduleId: string | number,
): FivePicSwiperTrackingParams => {
  return useMemo(
    () => ({
      module_index: moduleIndex,
      module_name: componentId,
      module_id: String(moduleId),
      mkt_index: index,
      mkt_index_sub: '',
      mkt_jump_type: String(item.jump_type || ''),
      mkt_jump_content: String(item.Jump_target || ''),
      module_tab_name: '',
      coupon_id_list: '',
      coupon_name_list: '',
      shop_sku: String(item.sku?.shop_sku || ''),
      business_sku: String(item.sku?.business_sku || ''),
      product_sku: String(item.sku?.sku || ''),
      product_id: String(item.sku?.id || ''),
      product_name: String(item.sku?.short_name || ''),
      group_id: String(item.product_group?.id || ''),
      group_name: String(item.product_group?.name || ''),
      module_title: moduleTitle,
    }),
    [item, index, moduleIndex, componentId, moduleTitle, moduleId],
  );
};

// 统一埋点处理Hook
const useTrackingHandlers = (
  trackingParams: FivePicSwiperTrackingParams,
  onTrack?: (params: FivePicSwiperTrackingParams) => void,
  onExpTrack?: (params: FivePicSwiperTrackingParams) => void,
  onValidExpTrack?: (params: FivePicSwiperTrackingParams) => void,
) => {
  const [expRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView && onExpTrack) {
      try {
        onExpTrack(trackingParams);
      } catch (error) {
        logger.error('exposure tracking error:', error);
      }
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (onTrack) {
      try {
        onTrack(trackingParams);
      } catch (error) {
        logger.error('tracking error:', error);
      }
    }
  }, [onTrack, trackingParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(trackingParams),
  });

  return { expRef, handleClick, validExposureRef };
};

// 滑动项组件
const FivePicSwiperSlide = ({
  item,
  index,
  indexData,
  onTrack,
  onExpTrack,
  moduleIndex,
  onValidExpTrack,
}: {
  item: FivePicSwiperItem;
  index: number;
  indexData: FivePicSwiperIndexData;
  onTrack?: (params: FivePicSwiperTrackingParams) => void;
  onExpTrack?: (params: FivePicSwiperTrackingParams) => void;
  moduleIndex: number;
  onValidExpTrack?: (params: FivePicSwiperTrackingParams) => void;
}) => {
  // 获取埋点参数
  const trackingParams = useTrackingParams(
    item,
    index,
    moduleIndex,
    indexData?.component_id || 'multiSwipeSecond',
    indexData?.layout_title || '',
    indexData?.blocks_advances_id || '',
  );

  // 获取埋点处理函数
  const { expRef, handleClick, validExposureRef } = useTrackingHandlers(
    trackingParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  );

  // MagicJump props
  const jumpProps = useMemo(
    () => ({
      jump_type: item.jump_type,
      Jump_target: item.Jump_target,
      ...(item.sku?.shop_sku && { sku: { shop_sku: item.sku.shop_sku } }),
      ...(item.product_group?.id && {
        product_group: { id: String(item.product_group.id), path: item.product_group.path },
      }),
      ...(item.crowd_recommend?.id && { crowd_recommend: { id: String(item.crowd_recommend.id) } }),
    }),
    [item],
  );

  const fivePicSwiperSlideRef = useMergedRefs(expRef, validExposureRef);
  return (
    <div ref={fivePicSwiperSlideRef} className="relative">
      <MagicJump
        prefetch
        item={jumpProps}
        onBeforeJump={handleClick}
        aria-label={item.title}
        className="block"
      >
        <div className="relative">
          <Image
            src={item.jump_image || ''}
            alt={getAlt('FivePicSwiper', index, indexData)}
            width={400}
            height={600}
            loading={moduleIndex < 5 ? 'eager' : 'lazy'}
            className="w-full h-auto object-cover"
            style={{
              aspectRatio: item.jump_img_ratio || 'auto',
            }}
          />

          {/* 文字叠加层 - 参考Vue项目的样式 */}
          <div className="absolute bottom-[18px] lg:bottom-[32px] left-0 w-full flex flex-col items-center justify-center text-center px-[18px] lg:px-[32px] box-border">
            {/* 标题 */}
            {item.title && (
              <h3
                className="w-full font-bb text-[14px] lg:text-[18px] 2xl:text-[24px] leading-tight mb-0 truncate whitespace-pre-line"
                style={{ color: item.title_color || '#ffffff' }}
                dangerouslySetInnerHTML={{
                  __html: item.title,
                }}
              />
            )}

            {/* 描述 */}
            {item.des && (
              <h4
                className="w-full mt-[5px] lg:mt-[7px] 2xl:mt-[9px] text-[12px] lg:text-[14px] 2xl:text-[16px] leading-[16px] lg:leading-[18px] 2xl:leading-[24px] line-clamp-2 whitespace-pre-line break-words"
                style={{ color: item.des_color || '#ffffff' }}
                dangerouslySetInnerHTML={{
                  __html: item.des,
                }}
              />
            )}

            {/* 按钮 */}
            {item.btn_text && (
              <div
                className="flex items-center justify-center min-w-[60px] lg:min-w-[80px] 2xl:min-w-[120px] font-bb text-[12px] 2xl:text-[16px] h-[24px] 2xl:h-[40px] rounded-[13px] 2xl:rounded-[20px] px-[6px] lg:px-[8px] 2xl:px-[12px] box-border mt-[6px] lg:mt-[8px] 2xl:mt-[12px]"
                style={{
                  color: item.btn_word_color || '#ffffff',
                  backgroundColor: item.btn_bg_color || '#000000',
                }}
              >
                {item.btn_text}
              </div>
            )}
          </div>
        </div>
      </MagicJump>
    </div>
  );
};

/**
 * FivePicSwiper主组件 - PC端独有
 */
export default function FivePicSwiper({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: FivePicSwiperProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;

  // 获取跳转目标列表
  const jumpTargets = useMemo(() => {
    return indexData?.jump_target || [];
  }, [indexData?.jump_target]);

  // Swiper配置 - 参考Vue项目的配置
  const swiperOptions = useMemo(
    () => ({
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      slidesPerView: 'auto' as const,
      spaceBetween: 0,
      allowTouchMove: true,
      breakpoints: {
        1024: {
          allowTouchMove: jumpTargets.length >= 6, // 数据少于6个时禁用滑动
        },
      },
    }),
    [jumpTargets.length],
  );

  // 如果没有数据或不是PC端，不渲染组件
  if (!indexData || !jumpTargets.length || !isPC) {
    return null;
  }

  return (
    <div className="my-[30px] lg:my-[50px] 2xl:my-[80px]">
      {/* 标题和按钮区域 */}
      <div className="flex items-center justify-between mb-[24px] lg:mb-[32px] mx-[24px] lg:mx-[48px]">
        <HomePageTitle
          indexData={indexData}
          variant="inline"
          displayCondition="pc-only"
          titleTag="h2"
          subtitleTag="h4"
          hasButton={!!indexData.btn_text}
        />

        {/* 按钮部分 */}
        {indexData.btn_text && (
          <div className="flex-shrink-0">
            <MagicJump
              prefetch
              item={{
                jump_type: indexData.jump_type || '',
                Jump_target: indexData.Jump_target || '',
                product_group: {
                  id: indexData.product_group?.id as string,
                  path: indexData.product_group?.path,
                },
                sku: {
                  shop_sku: indexData.sku?.shop_sku,
                },
                crowd_recommend: {
                  id: indexData.crowd_recommend?.id as string,
                },
              }}
              className="inline-block"
            >
              <div
                className="flex items-center justify-center min-w-[7.2916666667vw] h-[36px] lg:h-[48px] rounded-[25px] px-[12px] lg:px-[15px] text-[12px] lg:text-[16px] border border-transparent"
                style={{
                  color: indexData.btn_word_color || '#ffffff',
                  backgroundColor: indexData.btn_bg_color || '#282828',
                  borderColor: indexData.btn_word_color || '#ffffff',
                }}
              >
                <span>{indexData.btn_text}</span>
                <RightArrowOutlined className="ml-[5px] text-[12px]" />
              </div>
            </MagicJump>
          </div>
        )}
      </div>

      {/* 轮播容器 */}
      <div className="relative">
        <Swiper {...swiperOptions} className="!pl-[24px] lg:!pl-[48px] !pr-[24px] lg:!pr-[48px]">
          {jumpTargets.map((item, index) => (
            <SwiperSlide
              key={item.id || index}
              className={`!w-[19.29%] !mr-[0.877%] last:!mr-0 !min-w-[252px] lg:!min-w-[auto]`}
            >
              <FivePicSwiperSlide
                item={item}
                index={index}
                indexData={indexData}
                onTrack={onTrack}
                onExpTrack={onExpTrack}
                moduleIndex={moduleIndex}
                onValidExpTrack={onValidExpTrack}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}
